import React, { useEffect, useState } from "react";
import { MessageSquareOff, PhoneOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import DncCheckPanel, { DNC_STATUS_LABELS } from "@/components/compliance/DncCheckPanel";
import ContactPhone from "@/model/interface/ContactPhone";
import type { DncType } from "@/model/interface/DncEntry";
import { DncListStatus } from "@/enum/DncListStatus";
import { useAddDncEntryMutation, useGetDncEntriesQuery, useRemoveFromDncListMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";

interface ContactDncButtonProps {
  phones: ContactPhone[];
}

const RECHECK_AFTER_DAYS = 31;

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

const LISTED_STATUSES: (DncListStatus | null | undefined)[] = [
  DncListStatus.NATIONAL_LISTED,
  DncListStatus.STATE_LISTED,
];

export default function ContactDncButton({ phones }: ContactDncButtonProps) {
  const [open, setOpen] = useState(false);
  const [selectedNumbers, setSelectedNumbers] = useState<string[]>([]);
  const numbers = phones
    .map((phone) => ({ phone, number: phone.phoneNumberE164 || phone.phoneNumber || "" }))
    .filter(({ number }) => number.trim().length > 0);
  const { data: dncEntries } = useGetDncEntriesQuery(
    { phoneNumbers: numbers.map(({ number }) => number) },
    { skip: !open || numbers.length === 0 },
  );
  const [addDncEntry, { isLoading: isAdding }] = useAddDncEntryMutation();
  const [removeFromDncList, { isLoading: isRemoving }] = useRemoveFromDncListMutation();

  const entriesFor = (number: string) =>
    dncEntries?.items.filter((entry) => entry.phoneNumberE164 === number) ?? [];

  const isOnList = (number: string, type: DncType) =>
    entriesFor(number).some((entry) => entry.type === type || entry.type === "BOTH");

  const isLitigatorNumber = (phone: ContactPhone, number: string) =>
    !!phone.litigator ||
    phone.dncListStatus === DncListStatus.LITIGATOR ||
    entriesFor(number).some((entry) => entry.source === "LITIGATOR");

  const isStaleListing = (phone: ContactPhone) =>
    LISTED_STATUSES.includes(phone.dncListStatus) &&
    (!phone.federalDncCheckedAt ||
      Date.now() - new Date(phone.federalDncCheckedAt).getTime() > RECHECK_AFTER_DAYS * DAY_IN_MILLISECONDS);

  const isCheckable = (phone: ContactPhone, number: string) => {
    if (isLitigatorNumber(phone, number)) {
      return false;
    }
    return !LISTED_STATUSES.includes(phone.dncListStatus) || isStaleListing(phone);
  };

  const statusLabel = (phone: ContactPhone) => {
    if (!phone.dncListStatus) {
      return "Not checked";
    }
    if (isStaleListing(phone)) {
      return `${DNC_STATUS_LABELS[phone.dncListStatus]}, last checked over ${RECHECK_AFTER_DAYS} days ago`;
    }
    return DNC_STATUS_LABELS[phone.dncListStatus];
  };

  const toggleSelected = (number: string, selected: boolean) => {
    setSelectedNumbers((current) =>
      selected ? [...current, number] : current.filter((selectedNumber) => selectedNumber !== number),
    );
  };

  const handleToggle = async (number: string, type: DncType) => {
    const listName = type === "DNC" ? "Do Not Call" : "Do Not Text";
    try {
      if (isOnList(number, type)) {
        await removeFromDncList({ phoneNumber: number, type }).unwrap();
        toast.success(`${number} removed from ${listName}`);
      } else {
        await addDncEntry({ phoneNumberE164: number, type }).unwrap();
        toast.success(`${number} added to ${listName}`);
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, `Failed to update ${listName}`));
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedNumbers(
        phones
          .filter((phone) => !phone.dncListStatus)
          .map((phone) => phone.phoneNumberE164 || phone.phoneNumber || "")
          .filter((number) => number.trim().length > 0),
      );
    }
  }, [open]);

  return (
    <>
      <Button variant="outline" size="sm" disabled={numbers.length === 0} onClick={() => setOpen(true)}>
        <ShieldCheck className="h-3.5 w-3.5 mr-1" />
        DNC/DNT
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Do Not Call and Do Not Text</DialogTitle>
            <DialogDescription>
              Add numbers to your Do Not Call or Do Not Text list, or tick the numbers you want to check against the
              national and state Do Not Call lists and known litigator lists.
            </DialogDescription>
          </DialogHeader>

          <ul className="divide-y rounded-md border">
            {numbers.map(({ phone, number }) => {
              const litigator = isLitigatorNumber(phone, number);
              const onDncList = isOnList(number, "DNC");
              const onDntList = isOnList(number, "DNT");
              const checkable = isCheckable(phone, number);
              return (
                <li key={number} className="grid grid-cols-[auto_1fr] items-center gap-x-3 p-3">
                  <Checkbox
                    id={`dnc-check-${number}`}
                    aria-label={`Select ${phone.phoneNumber || number} for a Do Not Call check`}
                    className="h-5 w-5"
                    checked={checkable && selectedNumbers.includes(number)}
                    disabled={!checkable}
                    onCheckedChange={(value) => toggleSelected(number, value === true)}
                  />
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <label
                        htmlFor={`dnc-check-${number}`}
                        className="flex items-center gap-2 text-sm font-medium tabular-nums"
                      >
                        {phone.phoneType && <span className="text-muted-foreground">{phone.phoneType}</span>}
                        {phone.phoneNumber || number}
                      </label>
                      <span className="text-xs text-muted-foreground">{statusLabel(phone)}</span>
                    </div>
                    {litigator ? (
                      <p className="text-sm text-muted-foreground">Litigator, blocked</p>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          variant={onDncList ? "secondary" : "outline"}
                          disabled={!dncEntries || isAdding || isRemoving}
                          onClick={() => void handleToggle(number, "DNC")}
                        >
                          <PhoneOff className="h-3.5 w-3.5 mr-1" />
                          {onDncList ? "Remove from Do Not Call" : "Add to Do Not Call"}
                        </Button>
                        {onDncList ? (
                          <span className="text-xs text-muted-foreground">Texts blocked by Do Not Call</span>
                        ) : (
                          <Button
                            size="sm"
                            variant={onDntList ? "secondary" : "outline"}
                            disabled={!dncEntries || isAdding || isRemoving}
                            onClick={() => void handleToggle(number, "DNT")}
                          >
                            <MessageSquareOff className="h-3.5 w-3.5 mr-1" />
                            {onDntList ? "Remove from Do Not Text" : "Add to Do Not Text"}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Check {selectedNumbers.length} selected number{selectedNumbers.length === 1 ? "" : "s"}
            </h3>
            {open && <DncCheckPanel key={selectedNumbers.join(",")} phoneNumbers={selectedNumbers} />}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
